// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AxisSummary from "../../shared/AxisSummary";
import CautionsList from "../../shared/CautionsList";
import { FigureRow, Figures } from "../../shared/FigureRow";
import SummaryCard, { NothingToSummarise } from "../../shared/SummaryCard";
import FusionalRangeChart from "./FusionalRangeChart";
import NearPointChart from "./NearPointChart";

// Constants
import { NEAR_TEST_DISTANCE_CM } from "src/constants/enums";

// Types
import {
  ExamSectionReportType,
  FusionalAmplitudesSummaryType,
  FusionalRangeSummaryType,
} from "src/services/exams/types";

// Utils
import { num } from "../../shared/formatters";

type SummaryType = NonNullable<
  ExamSectionReportType["postProcessing"]["summary"]
>;
type VergenceFunctionSummaryType = SummaryType["VergenceFunction"];
type RangeSummaryType = FusionalRangeSummaryType | null;

type SummarySectionType = {
  summary: SummaryType | null | undefined;
};

const MODULE_LABELS: Record<string, string> = {
  FusionalAmplitudes: "Fusional Amplitudes",
  VergenceFunction: "Vergence Function",
};

const RangeValue = ({ value }: { value: RangeSummaryType }) => {
  const { t } = useTranslation();

  if (!value) {
    return <Typography fontSize="12px">{t("N/A")}</Typography>;
  }

  return (
    <Box display="inline-flex" flexWrap="wrap" alignItems="center" gap="4px">
      <Typography fontSize="12px">
        {num(value.breakPrismDioptres)} /{" "}
        {num(value.recoveryPrismDioptres, 1, "Δ")}
      </Typography>
      {value.isLowerBound && (
        <Typography fontSize="12px">{t("(lower bound)")}</Typography>
      )}
    </Box>
  );
};

const FusionalAmplitudesFigures = ({
  data,
}: {
  data: FusionalAmplitudesSummaryType;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <FusionalRangeChart
        convergence={data.convergence}
        divergence={data.divergence}
      />
      <Figures>
        <FigureRow
          label={t("Fusion Present")}
          value={
            <Typography fontSize="12px">
              {data.fusionPresent ? t("Yes") : t("No")}
            </Typography>
          }
        />
        <FigureRow
          label={t("Fusion Quality")}
          value={
            <Typography fontSize="12px">
              {data.fusionQuality ? t(data.fusionQuality) : t("N/A")}
            </Typography>
          }
        />
        <FigureRow
          label={t("Suppressed Eye")}
          value={
            <Typography fontSize="12px">
              {data.suppressedEye ? t(data.suppressedEye) : t("N/A")}
            </Typography>
          }
        />
        <FigureRow
          label={t("Convergence (break/recovery)")}
          value={<RangeValue value={data.convergence} />}
        />
        <FigureRow
          label={t("Divergence (break/recovery)")}
          value={<RangeValue value={data.divergence} />}
        />
        <FigureRow
          label={t("Vertical Total")}
          value={num(data.verticalTotal?.prismDioptres, 1, "Δ")}
        />
      </Figures>
    </>
  );
};

// One card per distance, each with its own cautions. A distance with nothing to report (the
// summary is null for a run that didn't complete) still gets its card, so it's clear it's missing.
const FusionalAmplitudesCard = ({
  distanceLabel,
  data,
}: {
  distanceLabel: string;
  data: FusionalAmplitudesSummaryType | null | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <SummaryCard
      title={`${t(MODULE_LABELS.FusionalAmplitudes)} - ${distanceLabel}`}
      cautions={data?.cautions ?? null}
    >
      {!data ? (
        <NothingToSummarise />
      ) : (
        <FusionalAmplitudesFigures data={data} />
      )}
    </SummaryCard>
  );
};

const VergenceFunctionCard = ({
  data,
}: {
  data: VergenceFunctionSummaryType;
}) => {
  const { t } = useTranslation();
  return (
    <SummaryCard
      title={t(MODULE_LABELS.VergenceFunction)}
      cautions={data?.cautions ?? null}
    >
      {!data ? (
        <NothingToSummarise />
      ) : (
        <>
          <NearPointChart nearPoint={data.nearPoint} />
          <Figures>
            <FigureRow
              label={t("Near Point Break")}
              value={
                data.nearPoint
                  ? num(data.nearPoint.breakCentimetres, 1, " cm")
                  : t("N/A")
              }
            />
            <FigureRow
              label={t("Near Point Recovery")}
              value={
                data.nearPoint
                  ? num(data.nearPoint.recoveryCentimetres, 1, " cm")
                  : t("N/A")
              }
            />
            <FigureRow
              label={t("Reached the Nose")}
              value={
                <Typography fontSize="12px">
                  {data.nearPoint
                    ? data.nearPoint.reachedTheNose
                      ? t("Yes")
                      : t("No")
                    : t("N/A")}
                </Typography>
              }
            />
            <FigureRow
              label={t("Recedes")}
              value={
                <Typography fontSize="12px">
                  {data.nearPoint
                    ? data.nearPoint.recedes
                      ? t("Yes")
                      : t("No")
                    : t("N/A")}
                </Typography>
              }
            />
            <FigureRow
              label={t("Facility")}
              value={
                data.facility
                  ? num(data.facility.cyclesPerMinute, 1, " cycles/min")
                  : t("N/A")
              }
            />
            <FigureRow
              label={t("Limiting Direction")}
              value={
                <Typography fontSize="12px">
                  {data.facility?.limitingDirection
                    ? t(data.facility.limitingDirection)
                    : t("N/A")}
                </Typography>
              }
            />
            <FigureRow
              label={t("Distance (Horizontal)")}
              value={
                data.distanceDeviation ? (
                  <AxisSummary axis={data.distanceDeviation.horizontal} />
                ) : (
                  <Typography fontSize="12px">{t("N/A")}</Typography>
                )
              }
            />
            <FigureRow
              label={t("Distance (Vertical)")}
              value={
                data.distanceDeviation ? (
                  <AxisSummary axis={data.distanceDeviation.vertical} />
                ) : (
                  <Typography fontSize="12px">{t("N/A")}</Typography>
                )
              }
            />
            <FigureRow
              label={t("Near (Horizontal)")}
              value={
                data.nearDeviation ? (
                  <AxisSummary axis={data.nearDeviation.horizontal} />
                ) : (
                  <Typography fontSize="12px">{t("N/A")}</Typography>
                )
              }
            />
            <FigureRow
              label={t("Near (Vertical)")}
              value={
                data.nearDeviation ? (
                  <AxisSummary axis={data.nearDeviation.vertical} />
                ) : (
                  <Typography fontSize="12px">{t("N/A")}</Typography>
                )
              }
            />
            <FigureRow
              label={t("AC/A Ratio")}
              value={data.aca ? `${num(data.aca.ratio)}` : t("N/A")}
            />
            <FigureRow
              label={t("AC/A Validity")}
              value={
                <Typography fontSize="12px">
                  {data.aca ? t(data.aca.validity) : t("N/A")}
                </Typography>
              }
            />
          </Figures>
        </>
      )}
    </SummaryCard>
  );
};

// The technician view: the few headline numbers read off each module, built straight from
// postProcessing.summary rather than the verbose per-window modules block.
const SummarySection = ({ summary }: SummarySectionType) => {
  const { t } = useTranslation();

  if (!summary) return null;

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap="16px" sx={{ mb: "8px" }}>
        <Box display="flex" flexDirection="column" gap="4px" sx={{ flex: 1 }}>
          <Typography
            fontSize="12px"
            color="#5B6D7A"
            sx={{ textTransform: "uppercase" }}
          >
            {t("Section Cautions")}
          </Typography>
          <CautionsList cautions={summary.cautions} />
        </Box>
        <Box display="flex" flexDirection="column" gap="4px" sx={{ flex: 1 }}>
          <Typography
            fontSize="12px"
            color="#5B6D7A"
            sx={{ textTransform: "uppercase" }}
          >
            {t("Incomplete Modules")}
          </Typography>
          {!summary.incompleteModules ||
          summary.incompleteModules.length === 0 ? (
            <Typography fontSize="12px">{t("N/A")}</Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap="4px">
              {summary.incompleteModules.map((key) => (
                <Typography key={key} sx={{ fontSize: "12px" }}>
                  {t(MODULE_LABELS[key] ?? key)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <FusionalAmplitudesCard
          distanceLabel={t("Far")}
          data={summary.FusionalAmplitudes?.far}
        />
        <FusionalAmplitudesCard
          distanceLabel={t("Near ({{distance}} cm)", {
            distance: NEAR_TEST_DISTANCE_CM,
          })}
          data={summary.FusionalAmplitudes?.near}
        />
        <VergenceFunctionCard data={summary.VergenceFunction} />
      </Box>
    </Box>
  );
};

export default SummarySection;
